import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Field, PageHeader } from '../components/page';
import { ActionError, QueryView } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/input';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { useApiMutation, useApiQuery } from '../lib/query';
import type { Template, TemplatePreview } from '../lib/types';
import { VARIABLES_HINT } from './scenarios';

// Éditeur de modèle (BA-024) : corps avec variables, aperçu rendu avec des données fictives.

function Editor({ template }: { template: Template }) {
  const t = useI18n().t;
  const auth = useAuth();
  const navigate = useNavigate();
  const editable =
    auth.can('administer') && (template.status === 'draft' || template.status === 'rejected');
  const [body, setBody] = useState(template.body);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useApiMutation<{ body: string }, Template>('PATCH', `/v1/templates/${template.id}`, {
    invalidate: ['/v1/templates'],
    onSuccess: () => setSaved(true),
  });
  const render = useApiMutation<object, TemplatePreview>(
    'POST',
    `/v1/templates/${template.id}/preview`,
    {
      onSuccess: setPreview,
    },
  );
  const dir = template.language === 'ar' ? 'rtl' : 'ltr';
  const client = useQueryClient();
  const review = useMutation<Template, Error, 'submit' | 'sync'>({
    mutationFn: (kind) =>
      auth.api.request<Template>('POST', `/v1/templates/${template.id}/${kind}`),
    onSuccess: (updated) => client.setQueryData([`/v1/templates/${template.id}`, {}], updated),
  });

  return (
    <>
      <PageHeader
        title={template.name}
        subtitle={
          <span className="flex gap-2">
            <Badge>{t.channels[template.channel]}</Badge>
            <Badge>{t.languages[template.language]}</Badge>
            <Badge tone="info">{t.scenarios.templateStatuses[template.status]}</Badge>
          </span>
        }
        actions={
          auth.can('administer') ? (
            <>
              {template.status === 'draft' || template.status === 'rejected' ? (
                <Button
                  size="sm"
                  disabled={review.isPending}
                  onClick={() => review.mutate('submit')}
                >
                  {t.templateReview.submit}
                </Button>
              ) : null}
              {template.connectTemplateId ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={review.isPending}
                  onClick={() => review.mutate('sync')}
                >
                  {t.templateReview.sync}
                </Button>
              ) : null}
              <ConfirmButton
                label={t.common.delete}
                title={t.common.delete}
                message={template.name}
                variant="destructive"
                onConfirm={async () => {
                  await auth.api.request('DELETE', `/v1/templates/${template.id}`);
                  await navigate({ to: '/scenarios', search: { tab: 'modeles' } });
                }}
              />
            </>
          ) : null
        }
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardContent className="flex flex-col gap-3">
            {!editable ? (
              <p className="text-sm text-muted-foreground">{t.scenarios.readOnlyTemplate}</p>
            ) : null}
            {template.reviewNote ? (
              <p role="alert" className="text-sm text-destructive">
                {t.templateReview.reviewNote} : {template.reviewNote}
              </p>
            ) : null}
            <ActionError error={review.error} />
            <Field label={t.scenarios.body} hint={`${t.scenarios.variables} : ${VARIABLES_HINT}`}>
              <Textarea
                rows={8}
                dir={dir}
                value={body}
                disabled={!editable}
                onChange={(e) => {
                  setSaved(false);
                  setBody(e.target.value);
                }}
              />
            </Field>
            <div className="flex flex-wrap items-center gap-2">
              {editable ? (
                <Button
                  disabled={save.isPending || body.trim() === ''}
                  onClick={() => save.mutate({ body })}
                >
                  {t.common.save}
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => render.mutate({})}>
                {t.scenarios.renderPreview}
              </Button>
              {saved ? (
                <span role="status" className="text-sm text-success">
                  {t.common.saved}
                </span>
              ) : null}
            </div>
            <ActionError error={save.error ?? render.error} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t.scenarios.renderPreview}</CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <>
                <p
                  dir={dir}
                  className="rounded-lg bg-success-soft p-3 text-sm whitespace-pre-wrap"
                  data-testid="template-preview"
                >
                  {preview.text}
                </p>
                {preview.missing.length ? (
                  <p className="mt-2 text-xs text-warning">
                    {t.scenarios.missingVariables} : {preview.missing.join(', ')}
                  </p>
                ) : null}
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export function TemplateEditorPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const query = useApiQuery<Template>(`/v1/templates/${id}`);
  return (
    <QueryView query={query}>
      {(template) => <Editor key={template.id} template={template} />}
    </QueryView>
  );
}
